import { LogoIcon } from "@/components/atoms/LogoIcon";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SixDigitInput from "../components/atoms/SixDigitInput";
import { useAuth } from "../contexts/AuthContext";
import {
  confirmForgotPassword,
  forgotPassword,
} from "../lib/services/authService";

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/trees";

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (error: any) {
      // Handle unverified user - redirect to confirmation page
      // Check both error.name and error message for compatibility with different Amplify versions
      const isUnconfirmedUser =
        error.name === "UserNotConfirmedException" ||
        error.message?.toLowerCase().includes("not confirmed") ||
        error.message?.toLowerCase().includes("not verified");

      if (isUnconfirmedUser) {
        const userEmail = error.username || email;
        // Try to resend confirmation code
        try {
          const { resendConfirmationCode } = await import(
            "../lib/services/authService"
          );
          await resendConfirmationCode(userEmail);
        } catch (resendError) {
          // Log but don't block - user can resend from confirmation page
          console.warn("Failed to resend confirmation code:", resendError);
        }
        // Always redirect to confirmation page
        navigate("/confirm", {
          state: {
            email: userEmail,
            fromLogin: true,
          },
        });
        return;
      }

      setError(error.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address first");
      return;
    }

    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      await forgotPassword(email);
      setSuccessMessage("Password reset code has been sent to your email");
      setIsPasswordReset(true);
    } catch (error: any) {
      setError(error.message || "Failed to send password reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    setLoading(true);

    try {
      await confirmForgotPassword(email, resetCode, newPassword);
      setSuccessMessage(
        "Password reset successful! You can now sign in with your new password.",
      );
      setIsPasswordReset(false);
      // Clear the form
      setResetCode("");
      setNewPassword("");
      setConfirmNewPassword("");
      setPassword("");
    } catch (error: any) {
      setError(error.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="80vh"
      >
        <Card sx={{ width: "100%", maxWidth: 400 }}>
          <CardContent
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <LogoIcon
              src="/images/Decision Node logo text bottom.png"
              lightSrc="/images/Decision Node logo text bottom.png"
              width={"8em"}
              height={"8em"}
              alt="Decision Node Logo"
            />
            <Typography
              variant="h4"
              component="h1"
              textAlign="center"
              gutterBottom
            >
              Return to confidence
            </Typography>

            <Typography
              variant="h6"
              textAlign="center"
              color="primary.main"
              gutterBottom
            >
              {isPasswordReset
                ? "Enter the 6-digit code sent to your email"
                : "Sign in to your account"}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={
                isPasswordReset ? handleConfirmPasswordReset : handleSignIn
              }
              sx={{ width: "100%" }}
            >
              {isPasswordReset ? (
                <>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                    disabled
                    autoComplete="email"
                  />
                  <SixDigitInput
                    value={resetCode}
                    onChange={setResetCode}
                    error={!!error && error.toLowerCase().includes("code")}
                    disabled={loading}
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    margin="normal"
                    required
                    autoComplete="new-password"
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    margin="normal"
                    required
                    autoComplete="new-password"
                  />
                </>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    margin="normal"
                    required
                    autoComplete="email"
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    margin="normal"
                    required
                    autoComplete="current-password"
                  />
                </>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading
                  ? "Loading..."
                  : isPasswordReset
                    ? "Reset Password"
                    : "Sign In"}
              </Button>

              {!isPasswordReset && (
                <Button
                  fullWidth
                  variant="text"
                  size="medium"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  Forgot Password?
                </Button>
              )}

              {isPasswordReset && (
                <Button
                  fullWidth
                  variant="text"
                  size="medium"
                  onClick={() => {
                    setIsPasswordReset(false);
                    setError("");
                    setSuccessMessage("");
                    setResetCode("");
                    setNewPassword("");
                    setConfirmNewPassword("");
                  }}
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  Back to Sign In
                </Button>
              )}
            </Box>

            <Box textAlign="center">
              {!isPasswordReset && (
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate("/signup")}
                >
                  Don't have an account? Sign up
                </Link>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LoginPage;
